import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Wallet, 
  Home, 
  User, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  X,
  Check
} from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentProfileSafe, getCurrentUserId, supabase, Account, Flat, Owner } from '../lib/supabase';
import AuditService from '../services/AuditService';
import toast from 'react-hot-toast';
import { matchesAccountSearch } from '../lib/accountSearch';
import { calculateBalanceFromTransactions, getBalanceColorClass, getBalanceStatus } from '../lib/balanceUtils';

interface AccountWithDetails extends Account {
  flat?: Flat;
  owners?: Owner[];
  balance?: number;
}

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [accountRole, setAccountRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [selectedFlatId, setSelectedFlatId] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [openingBalance, setOpeningBalance] = useState('0');
  const navigate = useNavigate();

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const profile = await getCurrentProfileSafe();
      setAccountRole(profile?.role || 'staff');

      const id = await getCurrentBodyCorporateId();
      setBodyCorporateId(id);
      await fetchAccounts(id);
      await fetchFlats(id);
      await fetchOwners(id);
    };

    load();
  }, []);

  const fetchAccounts = async (id: string | null) => {
    setLoading(true);
    if (!id) {
      setAccounts([]);
      setLoading(false);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    try {
      // Get accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from('accounts')
        .select('*')
        .eq('body_corporate_id', id)
        .order('account_number');

      if (accountsError) throw accountsError;

      // Get flats
      const { data: flatsData, error: flatsError } = await supabase
        .from('flats')
        .select('*')
        .eq('body_corporate_id', id);

      if (flatsError) throw flatsError;

      // Get account owners
      const { data: accountOwnersData, error: aoError } = await supabase
        .from('account_owners')
        .select('account_id, owner_id')
        .eq('is_primary', true);

      if (aoError) throw aoError;

      // Get owners
      const { data: ownersData, error: ownersError } = await supabase
        .from('owners')
        .select('*')
        .eq('body_corporate_id', id);

      if (ownersError) throw ownersError;

      // Get balances for each account
      const accountsWithDetails = await Promise.all(
        (accountsData || []).map(async (account: Account) => {
          const flat = flatsData?.find((f: Flat) => f.id === account.flat_id);
          const accountOwner = accountOwnersData?.find((ao: { account_id: string; owner_id: string }) => ao.account_id === account.id);
          const owner = accountOwner ? ownersData?.find((o: Owner) => o.id === accountOwner.owner_id) : null;

          // Get balance from an RPC when available; otherwise fall back to the posted
          // transaction ledger so the displayed balance remains correct without the DB function.
          let balance = 0;
          const { data: balanceData, error: balanceError } = await supabase
            .rpc('get_account_balance', { p_account_id: account.id });

          if (!balanceError && typeof balanceData === 'number') {
            balance = balanceData;
          } else {
            const { data: transactionRows, error: txError } = await supabase
              .from('transactions')
              .select('debit_amount, credit_amount, status')
              .eq('account_id', account.id)
              .eq('status', 'posted');

            if (!txError && transactionRows) {
              balance = calculateBalanceFromTransactions(transactionRows);
            }
          }

          return {
            ...account,
            flat,
            owners: owner ? [owner] : [],
            balance,
          };
        })
      );

      setAccounts(accountsWithDetails);
    } catch (error) {
      console.error(error);
      toast.error('Error fetching accounts');
    }
    setLoading(false);
  };

  const fetchFlats = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('flats')
      .select('*')
      .eq('body_corporate_id', id)
      .eq('status', 'occupied')
      .order('flat_number');

    if (!error && data) setFlats(data);
  };

  const fetchOwners = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('owners')
      .select('*')
      .eq('body_corporate_id', id)
      .eq('is_active', true)
      .order('full_name');

    if (!error && data) setOwners(data);
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (accountRole === 'viewer') {
      toast.error('Viewer accounts can only view accounts.');
      return;
    }

    if (!selectedFlatId || !selectedOwnerId) {
      toast.error('Please select a flat and owner');
      return;
    }

    // Check if flat already has an account
    const { data: existingAccount } = await supabase
      .from('accounts')
      .select('id')
      .eq('flat_id', selectedFlatId)
      .eq('status', 'active')
      .single();

    if (existingAccount) {
      toast.error('This flat already has an active account');
      return;
    }

    // Create account
    const { data: accountData, error: accountError } = await supabase
      .from('accounts')
      .insert({
        body_corporate_id: bodyCorporateId,
        flat_id: selectedFlatId,
        account_number: accountNumber || `ACC-${Date.now().toString().slice(-6)}`,
        opened_on: new Date().toISOString().split('T')[0],
        status: 'active',
      })
      .select()
      .single();

    if (accountError) {
      toast.error('Error creating account');
      console.error(accountError);
      return;
    }

    const userId = await getCurrentUserId();
    if (bodyCorporateId && userId) {
      await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'account', accountData.id, undefined, accountData);
    }

    // Link owner to account
    const { error: linkError } = await supabase
      .from('account_owners')
      .insert({
        account_id: accountData.id,
        owner_id: selectedOwnerId,
        start_date: new Date().toISOString().split('T')[0],
        is_primary: true,
      });

    if (linkError) {
      toast.error('Error linking owner to account');
      console.error(linkError);
      return;
    }

    if (bodyCorporateId && userId) {
      await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'account_owner', `${accountData.id}:${selectedOwnerId}`, undefined, {
        account_id: accountData.id, owner_id: selectedOwnerId, is_primary: true,
      });
    }

    const openingAmount = parseFloat(openingBalance) || 0;
    if (openingAmount !== 0) {
      const { data: openingTransaction, error: openingBalanceError } = await supabase
        .from('transactions')
        .insert({
          body_corporate_id: bodyCorporateId,
          account_id: accountData.id,
          transaction_date: new Date().toISOString().split('T')[0],
          transaction_type: 'opening_balance',
          description: 'Opening Balance',
          reference_number: `OB-${accountData.account_number}`,
          debit_amount: openingAmount > 0 ? openingAmount : 0,
          credit_amount: openingAmount < 0 ? Math.abs(openingAmount) : 0,
          status: 'posted',
        }).select().single();

      if (openingBalanceError) {
        toast.error('Account created, but opening balance could not be saved');
        console.error(openingBalanceError);
      } else if (bodyCorporateId && userId && openingTransaction) {
        await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'transaction', openingTransaction.id, undefined, openingTransaction);
      }
    }

    toast.success('Account created successfully');
    setShowModal(false);
    fetchAccounts(bodyCorporateId);
  };

  const filteredAccounts = accounts.filter(account => {
    return matchesAccountSearch(account, searchTerm);
  });

  const getBalanceColor = getBalanceColorClass;

  const getBalanceLabel = getBalanceStatus;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Accounts</h1>
          <p className="text-gray-500">Manage all levy accounts</p>
        </div>
        {accountRole !== 'viewer' && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 btn-primary"
          >
            <Plus size={20} />
            Create Account
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by account number, flat, or owner..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Cards */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading accounts...</div>
      ) : filteredAccounts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Wallet className="mx-auto text-gray-400" size={48} />
          <p className="mt-4 text-gray-500">No accounts found</p>
          <p className="text-sm text-gray-400">Create an account to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAccounts.map((account) => (
            <div
              key={account.id}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/accounts/${account.id}`)}
            >
              {/* Account Number */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                  {account.account_number}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  account.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {account.status}
                </span>
              </div>

              {/* Flat */}
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <Home size={16} className="text-gray-400" />
                <span className="font-medium">{account.flat?.flat_number || 'No flat'}</span>
                {account.flat?.participation_quota != null && (
                  <span className="text-xs text-gray-500">({account.flat.participation_quota}%)</span>
                )}
              </div>

              {/* Owner */}
              <div className="flex items-center gap-2 text-gray-600 mb-3">
                <User size={16} className="text-gray-400" />
                <span>{account.owners?.[0]?.full_name || 'No owner'}</span>
              </div>

              {/* Balance */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div>
                  <p className="text-sm text-gray-500">Balance</p>
                  <p className={`text-xl font-bold ${getBalanceColor(account.balance)}`}>
                    R {account.balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {account.balance && account.balance > 0 ? (
                    <TrendingUp size={16} className="text-red-500" />
                  ) : account.balance && account.balance < 0 ? (
                    <TrendingDown size={16} className="text-green-500" />
                  ) : null}
                  <span className={`text-sm font-medium ${getBalanceColor(account.balance)}`}>
                    {getBalanceLabel(account.balance || 0)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Account Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Create New Account</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateAccount}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flat *
                  </label>
                  <select
                    required
                    value={selectedFlatId}
                    onChange={(e) => setSelectedFlatId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select a flat...</option>
                    {flats.map((flat) => (
                      <option key={flat.id} value={flat.id}>
                        {flat.flat_number} - {flat.physical_address || 'No address'}
                      </option>
                    ))}
                  </select>
                  {flats.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      No available flats. Please add a flat first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Owner *
                  </label>
                  <select
                    required
                    value={selectedOwnerId}
                    onChange={(e) => setSelectedOwnerId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select an owner...</option>
                    {owners.map((owner) => (
                      <option key={owner.id} value={owner.id}>
                        {owner.full_name} {owner.email ? `(${owner.email})` : ''}
                      </option>
                    ))}
                  </select>
                  {owners.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      No active owners. Please add an owner first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Leave blank for auto-generation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    placeholder="0.00 (negative means credit)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use a negative amount when the account starts in credit.</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                  disabled={!selectedFlatId || !selectedOwnerId}
                >
                  <Check size={20} />
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
